import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Info, AlertTriangle } from "lucide-react";
import img90 from "../../../assets/img90.png";
import SEO from "../../../components/SEO";

/* ---------------- utils ---------------- */
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function toNumberLoose(v) {
  if (v === null || v === undefined) return NaN;
  const s = String(v).replace(/[,$\s]/g, "");
  if (!s) return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

function numOrZero(v) {
  const n = toNumberLoose(v);
  return Number.isFinite(n) ? n : 0;
}

function formatCurrency(n) {
  const x = Number.isFinite(n) ? n : 0;
  return x.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
}

function percentFromGrowthValue(v) {
  const m = String(v || "").match(/_(\d+)$/);
  return m ? Number(m[1]) : 0;
}

function isObj(x) {
  return x && typeof x === "object" && !Array.isArray(x);
}

function findAnnualRowsDeep(root) {
  const queue = [{ node: root, depth: 0 }];
  const maxDepth = 10;

  const looksLikeYearRow = (row) => {
    if (!isObj(row)) return false;
    const keys = Object.keys(row).map((k) => k.toLowerCase());
    const hasYear =
      keys.includes("year_no") ||
      keys.includes("year") ||
      keys.includes("yearno");
    const hasAge =
      keys.includes("beneficiary_age") ||
      keys.includes("beneficiaryage") ||
      keys.includes("age");
    const hasTotal =
      keys.includes("total") ||
      keys.includes("cumulative_balance") ||
      keys.includes("balance");
    return hasYear || (hasAge && hasTotal);
  };

  while (queue.length) {
    const { node, depth } = queue.shift();
    if (depth > maxDepth) continue;

    if (Array.isArray(node) && node.length && looksLikeYearRow(node[0]))
      return node;

    if (Array.isArray(node)) {
      for (const it of node) queue.push({ node: it, depth: depth + 1 });
    } else if (isObj(node)) {
      for (const k of Object.keys(node))
        queue.push({ node: node[k], depth: depth + 1 });
    }
  }
  return [];
}

function pick(row, candidates, fallback = "") {
  const lowerMap = {};
  for (const k of Object.keys(row || {})) lowerMap[k.toLowerCase()] = row[k];

  for (const c of candidates) {
    const v = lowerMap[String(c).toLowerCase()];
    if (v !== undefined && v !== null) return v;
  }
  return fallback;
}

function downloadTextFile(filename, text, mime = "text/plain") {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function toCSV(rows, columns) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    const needs = /[",\n]/.test(s);
    const safe = s.replace(/"/g, '""');
    return needs ? `"${safe}"` : safe;
  };

  const header = columns.map((c) => esc(c.header)).join(",");
  const body = rows
    .map((r) => columns.map((c) => esc(c.value(r))).join(","))
    .join("\n");
  return `${header}\n${body}`;
}

function formatShortK(n) {
  const x = Number.isFinite(n) ? n : 0;
  const ax = Math.abs(x);

  if (ax >= 1_000_000) return `${(x / 1_000_000).toFixed(1)}M`;
  if (ax >= 1_000) return `${(x / 1_000).toFixed(1)}K`;
  return `${Math.round(x)}`;
}

/* --------- SVG helpers --------- */
function polarToCartesian(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}
function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
}

const inputClasses =
  "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm sm:text-base text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#00adef] focus:border-[#00adef]";
const labelClasses =
  "block text-sm sm:text-base font-medium text-gray-700 mb-1";

/* ---------------- Tooltip ---------------- */
function InfoTip({ text }) {
  const btnRef = useRef(null);
  const tipRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0,
    placement: "top",
    arrowLeft: 0,
  });

  const computePosition = () => {
    const btn = btnRef.current;
    const tip = tipRef.current;
    if (!btn || !tip) return;

    const r = btn.getBoundingClientRect();
    const tw = tip.offsetWidth || 288;
    const th = tip.offsetHeight || 90;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const centerX = r.left + r.width / 2;
    const desiredLeft = centerX - tw / 2;
    const left = clamp(desiredLeft, 12, vw - tw - 12);

    const topCandidate = r.top - th - 10;
    const placement = topCandidate < 12 ? "bottom" : "top";
    const top =
      placement === "top"
        ? clamp(topCandidate, 12, vh - th - 12)
        : clamp(r.bottom + 10, 12, vh - th - 12);

    const arrowLeft = clamp(centerX - left, 18, tw - 18);
    setPos({ top, left, placement, arrowLeft });
  };

  useEffect(() => {
    if (!open) return;

    const raf = requestAnimationFrame(() => computePosition());
    const onResize = () => computePosition();
    const onScroll = () => computePosition();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  return (
    <>
      <span
        className="relative inline-flex items-center align-middle"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          ref={btnRef}
          type="button"
          aria-label="Info"
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          className="ml-1 inline-flex items-center justify-center focus:outline-none"
        >
          <Info
            className="w-4 h-4 text-[#435D8C] cursor-pointer"
            aria-hidden="true"
          />
        </button>
      </span>

      {open &&
        createPortal(
          <div
            ref={tipRef}
            className="fixed z-[9999] w-64 sm:w-72 max-w-[90vw]"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="bg-black text-white text-xs leading-relaxed px-4 py-3 rounded-2xl shadow-lg">
              {text}
            </div>

            <div
              className={[
                "absolute w-3 h-3 bg-black rotate-45",
                pos.placement === "top" ? "-bottom-[6px]" : "-top-[6px]",
              ].join(" ")}
              style={{ left: pos.arrowLeft - 6 }}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

/* ---------------- ToggleButtonGroup ---------------- */
function ToggleButtonGroup({
  value,
  onChange,
  options,
  disabled = false,
  maxWidthClass = "sm:max-w-xs",
}) {
  return (
    <div
      className={[
        "inline-flex rounded-md overflow-hidden border border-gray-300 w-full max-w-full",
        maxWidthClass,
        disabled ? "opacity-70 cursor-not-allowed" : "",
      ].join(" ")}
      role="radiogroup"
    >
      {options.map((opt, idx) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => !disabled && onChange(opt.value)}
            className={[
              "flex-1 px-4 py-2 text-sm sm:text-base font-semibold transition-colors",
              active
                ? "bg-[#00adef] text-white"
                : "bg-[#e4e4e4] text-gray-700 hover:bg-gray-300",
              idx === 0 ? "border-r border-gray-300" : "",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Card ---------------- */
function SectionCard({ title, subtitle, children, right }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <div className="bg-white px-6 py-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold tracking-wide text-[#2f4f77]">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-1 text-sm text-gray-700">{subtitle}</p>
          ) : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>

      <div className="px-6 py-6">{children}</div>
    </div>
  );
}

/* ---------------- MiniDonut ---------------- */
function MiniDonut({ items }) {
  const total = items.reduce(
    (s, it) => s + (Number.isFinite(it.value) ? it.value : 0),
    0,
  );
  const safeTotal = total > 0 ? total : 1;

  const [tip, setTip] = useState({ show: false, text: "", x: 0, y: 0 });

  const size = 210;
  const cx = size / 2;
  const cy = size / 2;
  const r = 70;
  const stroke = 22;

  let start = 0;
  const arcs = items.map((it) => {
    const pct = (it.value / safeTotal) * 100;
    const sweep = (it.value / safeTotal) * 360;
    const end = start + sweep;
    const path = describeArc(cx, cy, r, start, end);
    const mid = start + sweep / 2;
    start = end;
    return { ...it, pct, path, mid };
  });

  return (
    <div className="relative w-full flex flex-col items-center">
      <div className="relative">
        <svg width={size} height={size} className="block">
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#e9eef6"
            strokeWidth={stroke}
          />
          {arcs.map((a) => (
            <path
              key={a.label}
              d={a.path}
              fill="none"
              stroke={a.color}
              strokeWidth={stroke}
              strokeLinecap="butt"
              onMouseEnter={(e) => {
                const rect =
                  e.currentTarget.ownerSVGElement.getBoundingClientRect();
                const pctText = `${Math.round(a.pct)}%`;
                setTip({
                  show: true,
                  text: `${a.label} : ${formatCurrency(a.value)} (${pctText})`,
                  x: rect.left + 16,
                  y: rect.top + 90,
                });
              }}
              onMouseMove={(e) => {
                const rect =
                  e.currentTarget.ownerSVGElement.getBoundingClientRect();
                setTip((t) => ({ ...t, x: rect.left + 16, y: rect.top + 90 }));
              }}
              onMouseLeave={() => setTip({ show: false, text: "", x: 0, y: 0 })}
            />
          ))}

          {arcs.map((a) => {
            const pct = Math.round(a.pct);
            if (pct <= 0) return null;

            const labelR = r;
            const pos = polarToCartesian(cx, cy, labelR, a.mid);

            return (
              <text
                key={`${a.label}-pct`}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="11"
                fontWeight="600"
                fill="#111827"
              >
                {pct}%
              </text>
            );
          })}
        </svg>

        {tip.show
          ? createPortal(
              <div
                className="fixed z-[9999] pointer-events-none"
                style={{ left: tip.x, top: tip.y }}
              >
                <div className="bg-white border border-gray-200 shadow-xl rounded-sm px-6 py-4 text-base text-gray-900">
                  {tip.text}
                </div>
              </div>,
              document.body,
            )
          : null}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-4 text-sm sm:text-base">
        {items.map((it) => (
          <div key={it.label} className="inline-flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: it.color }}
            />
            <span className="text-gray-900 font-semibold">{it.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- AreaLineChart ---------------- */
function AreaLineChart({ points }) {
  const wrapRef = useRef(null);
  const [tip, setTip] = useState({
    show: false,
    x: 0,
    y: 0,
    label: "",
    value: "",
  });

  const width = 820;
  const height = 260;
  const padL = 82;
  const padR = 18;
  const padT = 16;
  const padB = 60;

  const usable = Array.isArray(points)
    ? points.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
    : [];
  const xs = usable.map((p) => p.x);
  const ys = usable.map((p) => p.y);
  const minX = xs.length ? Math.min(...xs) : 0;
  const maxX = xs.length ? Math.max(...xs) : 1;
  const minY = 0;
  const maxY = ys.length ? Math.max(...ys) : 1;

  const xScale = (x) => {
    const t = (x - minX) / (maxX - minX || 1);
    return padL + t * (width - padL - padR);
  };
  const yScale = (y) => {
    const t = (y - minY) / (maxY - minY || 1);
    return height - padB - t * (height - padT - padB);
  };

  const pathLine = useMemo(() => {
    if (!usable.length) return "";
    return usable
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.x)} ${yScale(p.y)}`)
      .join(" ");
  }, [usable, minX, maxX, maxY]);

  const pathArea = useMemo(() => {
    if (!usable.length) return "";
    const first = usable[0];
    const last = usable[usable.length - 1];
    const topPath = usable
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.x)} ${yScale(p.y)}`)
      .join(" ");
    const bottom = `L ${xScale(last.x)} ${yScale(0)} L ${xScale(first.x)} ${yScale(0)} Z`;
    return `${topPath} ${bottom}`;
  }, [usable, minX, maxX, maxY]);

  const xTicks = useMemo(() => {
    const n = 6;
    const out = [];
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const v = minX + t * (maxX - minX);
      out.push(Math.round(v));
    }
    return Array.from(new Set(out));
  }, [minX, maxX]);

  const yTicks = useMemo(() => {
    const step = 200_000;
    const top = Math.max(step, Math.ceil((maxY || 0) / step) * step);
    const out = [];
    for (let v = 0; v <= top; v += step) out.push(v);
    return out;
  }, [maxY]);

  const onMove = (e) => {
    if (!wrapRef.current || !usable.length) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;

    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < usable.length; i++) {
      const px = xScale(usable[i].x);
      const d = Math.abs(px - mx);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const p = usable[bestIdx];
    const px = xScale(p.x);
    const py = yScale(p.y);

    setTip({
      show: true,
      x: rect.left + px + 14,
      y: rect.top + py - 24,
      label: `Âge ${p.x}`,
      value: `Solde : ${formatCurrency(p.y)}`,
    });
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        onMouseMove={onMove}
        onMouseLeave={() =>
          setTip({ show: false, x: 0, y: 0, label: "", value: "" })
        }
      >
        <defs>
          <linearGradient id="rdspArea" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#00adef" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#00adef" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {yTicks.map((v, i) => {
          const y = yScale(v);
          return (
            <line
              key={`gy-${i}`}
              x1={padL}
              x2={width - padR}
              y1={y}
              y2={y}
              stroke="#e5e7eb"
              strokeDasharray="4 4"
            />
          );
        })}
        {xTicks.map((v, i) => {
          const x = xScale(v);
          return (
            <line
              key={`gx-${i}`}
              x1={x}
              x2={x}
              y1={padT}
              y2={height - padB}
              stroke="#e5e7eb"
              strokeDasharray="4 4"
            />
          );
        })}

        <line
          x1={padL}
          x2={padL}
          y1={padT}
          y2={height - padB}
          stroke="#6b7280"
        />
        <line
          x1={padL}
          x2={width - padR}
          y1={height - padB}
          y2={height - padB}
          stroke="#6b7280"
        />

        {yTicks.map((v, i) => {
          const y = yScale(v);
          return (
            <text
              key={`yl-${i}`}
              x={padL - 10}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="12"
              fill="#374151"
            >
              {formatShortK(v)}
            </text>
          );
        })}

        {xTicks.map((v, i) => {
          const x = xScale(v);
          return (
            <text
              key={`xl-${i}`}
              x={x}
              y={height - padB + 24}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="12"
              fill="#374151"
            >
              {v}
            </text>
          );
        })}

        <text
          x={padL - 62}
          y={(padT + (height - padB)) / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="12"
          fill="#2f4f77"
          transform={`rotate(-90 ${padL - 62} ${(padT + (height - padB)) / 2})`}
          fontWeight="700"
        >
          Solde
        </text>

        <text
          x={(padL + (width - padR)) / 2}
          y={height - 10}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="12"
          fill="#2f4f77"
          fontWeight="700"
        >
          Âge du bénéficiaire
        </text>

        {usable.length ? <path d={pathArea} fill="url(#rdspArea)" /> : null}
        {usable.length ? (
          <path d={pathLine} fill="none" stroke="#1f4aa5" strokeWidth="4" />
        ) : null}

        {tip.show && usable.length
          ? (() => {
              const age = Number(
                String(tip.label || "")
                  .replace("Âge", "")
                  .trim(),
              );
              const p = usable.find((pp) => pp.x === age);
              if (!p) return null;
              const cx = xScale(p.x);
              const cy = yScale(p.y);
              return (
                <>
                  <line
                    x1={cx}
                    x2={cx}
                    y1={padT}
                    y2={height - padB}
                    stroke="#9ca3af"
                  />
                  <circle
                    cx={cx}
                    cy={cy}
                    r="6"
                    fill="#1f4aa5"
                    stroke="#ffffff"
                    strokeWidth="2"
                  />
                </>
              );
            })()
          : null}
      </svg>

      {tip.show
        ? createPortal(
            <div
              className="fixed z-[9999] pointer-events-none"
              style={{ left: tip.x, top: tip.y }}
            >
              <div className="bg-white border border-gray-200 shadow-xl rounded-sm px-6 py-4">
                <div className="text-base text-gray-900">{tip.label}</div>
                <div
                  className="text-base"
                  style={{ color: "#1f4aa5", marginTop: 6 }}
                >
                  {tip.value}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/* ---------------- Summary Card ---------------- */
function SummaryTile({ title, value }) {
  return (
    <div className="rounded-xl border border-[#d7e7ff] bg-[#f7fbff] px-5 py-4">
      <div className="text-sm text-[#5b6b86]">{title}</div>
      <div className="mt-2 text-lg sm:text-xl font-semibold text-gray-900">
        {value}
      </div>
    </div>
  );
}

/* ---------------- Main ---------------- */
export default function EinsuredRdspCalculator() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const RDSP_ESTIMATE_URL = import.meta.env.DEV
    ? "/einsured-www/api/rdspEstimate"
    : "https://backend.einsured.ca/api/rdspEstimate";

  const RDSP_REPORT_URL = "https://backend.einsured.ca/api/rdspCalculator";

  const growthOptions = [
    { label: "Très dynamique (11 %)", value: "very_aggressive_11" },
    { label: "Très dynamique modéré (9 %)", value: "more_aggressive_9" },
    { label: "Dynamique (7 %)", value: "aggressive_7" },
    { label: "Modéré (5 %)", value: "moderate_5" },
    { label: "Prudent (3 %)", value: "conservative_3" },
    { label: "Très prudent (1 %)", value: "more_conservative_1" },
  ];

  const [form, setForm] = useState({
    youAre: "Un particulier, un membre de la famille ou un représentant personnel",
    openedYear: "2026",
    age: "",
    income: "",
    dtc: "Non, ou incertain(e)",
    dtcFirstYear: "",

    yearlyContribution: "",
    stopContributionAge: "49",
    growthBefore: "moderate_5",
    growthAfter: "moderate_5",
    addLumpSum: "No",
    lumpSums: [],

    withdrawalStartAge: "60",
    addLumpWithdrawals: "No",
    lumpWithdrawals: [],
  });

  const [touched, setTouched] = useState({
    age: false,
    openedYear: false,
    dtcFirstYear: false,
    stopContributionAge: false,
    lumpAge: {},
    withdrawAge: {},
  });

  const [reportReady, setReportReady] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportRaw, setReportRaw] = useState(null);
  const [annualRows, setAnnualRows] = useState([]);

  const [estimateLoading, setEstimateLoading] = useState(false);
  const [estimateError, setEstimateError] = useState("");
  const [estimateGrant, setEstimateGrant] = useState(0);
  const [estimateBond, setEstimateBond] = useState(0);

  const [lastGeneratedSig, setLastGeneratedSig] = useState("");

  const [topPanelMode, setTopPanelMode] = useState("graphs");

  /* ---------------- Disclaimer ---------------- */
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  useEffect(() => {
    if (!disclaimerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [disclaimerOpen]);

  const disclaimerShort = (
    <>
      Ce calculateur est un outil éducatif. Les résultats sont des estimations
      seulement et peuvent différer du rendement réel de votre REEI. Lisez la
      clause de non-responsabilité complète{" "}
      <button
        type="button"
        onClick={() => setDisclaimerOpen(true)}
        className="font-semibold underline text-[#49628b] hover:text-[#163a80]"
      >
        (Cliquez ici)
      </button>
      .
    </>
  );

  const disclaimerFull = (
    <>
      <p className="mb-4">
        Tous les calculs et estimations fournis par ce calculateur sont à des
        fins informatives et illustratives seulement.
      </p>

      <p className="mb-4">
        Les résultats sont basés sur des formules et lignes directrices
        accessibles au public provenant des ressources officielles du
        gouvernement du Canada sur le REEI, incluant les programmes de la
        Subvention canadienne pour l'épargne-invalidité et du Bon canadien pour
        l'épargne-invalidité.
      </p>

      <p className="mb-1 font-semibold">
        Les montants réels des subventions, bons et placements peuvent varier
      </p>
      <p className="mb-2">selon des facteurs tels que :</p>

      <ul className="list-disc pl-5 space-y-1 mb-4">
        <li>la période d'approbation du certificat pour invalidités (CI) du bénéficiaire,</li>
        <li>le revenu familial pour chaque année admissible,</li>
        <li>le calendrier des cotisations, et</li>
        <li>les politiques du fournisseur du régime ou les exigences administratives.</li>
      </ul>

      <p className="mb-4">
        Ce calculateur suit le cadre fédéral du REEI, qui est uniforme dans
        toutes les provinces et tous les territoires pour les plafonds de
        cotisation, l'admissibilité aux subventions/bons et les règles de
        retrait (LDAP/DAP).
      </p>

      <p className="mb-2">
        Cependant, les programmes provinciaux de prestations d'invalidité
        peuvent traiter différemment l'épargne et les retraits du REEI :
      </p>

      <ul className="list-disc pl-5 space-y-1 mb-4">
        <li>
          Dans la plupart des provinces et territoires, les actifs et les
          retraits du REEI sont entièrement exempts d'affecter les prestations
          d'invalidité ou de soutien du revenu.
        </li>
        <li>
          Au Québec, certains programmes provinciaux de prestations peuvent
          partiellement comptabiliser les retraits du REEI comme revenu, ce qui
          pourrait affecter l'admissibilité ou les montants de paiement.
        </li>
      </ul>

      <p className="mb-4">
        Canada REEI et ses affiliés ne garantissent pas l'exactitude ou
        l'exhaustivité des calculs ou résultats produits par cet outil et
        n'assument aucune responsabilité pour les décisions financières prises
        sur la base de son utilisation.
      </p>

      <p className="mb-2">
        Pour des conseils personnalisés concernant votre admissibilité au REEI,
        vos cotisations ou les implications des retraits, veuillez consulter un
        conseiller financier agréé ou vous référer aux sources officielles du
        gouvernement du Canada.
      </p>

      <p className="text-sm text-gray-600">
        <a
          className="underline text-[#49628b] hover:text-[#163a80]"
          href="https://www.canada.ca/rdsp"
          target="_blank"
          rel="noreferrer"
        >
          www.canada.ca/rdsp
        </a>
      </p>
    </>
  );
  /* ---------------- Disclaimer ---------------- */

  const helpText = useMemo(
    () => ({
      youAre:
        "Sélectionnez le bénéficiaire, un membre de la famille ou un représentant juridique/du plan. Ceci n'affecte pas les calculs — c'est uniquement à titre indicatif.",
      openedYear:
        "Année d'ouverture du REEI. S'il n'est pas encore ouvert, choisissez l'année d'ouverture prévue. Les subventions/bons ne peuvent être versés avant l'ouverture du plan.",
      age: "Entrez l'âge actuel du titulaire du REEI. L'aide gouvernementale s'arrête l'année où il/elle atteint 49 ans ; les retraits commencent généralement vers 60 ans.",
      income:
        "Revenu net familial pour l'admissibilité aux subventions/bons. Si le bénéficiaire a 18 ans ou plus, utilisez son revenu net familial. Sinon, utilisez le revenu net familial des parents/tuteurs.",
      dtc: "Répondez « Oui » uniquement si le certificat pour invalidités (CI) a été approuvé pour au moins une année d'imposition.",
      dtcFirstYear:
        "Première année d'approbation du CI. Utilisée pour calculer le report des subventions/bons (max 10 années antérieures, selon les limites d'âge).",
      yearlyContribution: "Cotisation annuelle prévue.",
      stopContributionAge: "Âge prévu pour arrêter les cotisations. Doit être ≤ 59.",
      growthBefore: "Rendement annuel moyen supposé avant les retraits.",
      growthAfter: "Rendement annuel moyen supposé après le début des retraits.",
      addLumpSum: "Dépôts ponctuels. Si « Oui », vous pouvez ajouter jusqu'à 8 entrées.",
      lumpAmount: "Montant du dépôt ponctuel.",
      lumpAge: "Âge du bénéficiaire lors de l'année du dépôt (31 déc.).",
      withdrawalStartAge: "Moment où les retraits commencent.",
      addLumpWithdrawals: "Retraits ponctuels supplémentaires. Ajoutez jusqu'à 2 entrées.",
      withdrawAmount: "Montant du retrait ponctuel.",
      withdrawAge: "Âge du bénéficiaire lors de l'année du retrait (31 déc.).",

      reportYear: "Ligne de l'année de projection.",
      reportAge: "Âge du bénéficiaire pour cette année de projection.",
      reportContribution: "Cotisation annuelle prévue.",
      reportGrant: "Montant de la contrepartie gouvernementale.",
      reportBond: "Montant du bon gouvernemental.",
      reportLumpSumContribution: "Dépôt ponctuel au cours de cette année.",
      reportTotal: "Solde précédent + montants de l'année courante.",
      reportInterest: "Croissance annuelle générée.",
      reportLumpSumWithdrawal: "Retrait ponctuel.",
      reportLDAP: "Retrait annuel obligatoire débutant à 60 ans.",
      reportTotalWithdrawal: "LDAP + Retrait forfaitaire",
      reportPenalty: "Pénalité si retrait dans les 10 premières années.",
      reportBalance: "Solde de clôture après intérêts et retraits.",
    }),
    [],
  );

  const setField = (key, value) => {
    setForm((prev) => {
      if (key === "dtc" && value !== "Yes") {
        return { ...prev, dtc: value, dtcFirstYear: "" };
      }

      if (key === "addLumpSum") {
        if (value === "Yes") {
          const nextRows = prev.lumpSums?.length
            ? prev.lumpSums
            : [{ amount: "", age: "" }];
          return { ...prev, addLumpSum: "Yes", lumpSums: nextRows };
        }
        return { ...prev, addLumpSum: "No", lumpSums: [] };
      }

      if (key === "addLumpWithdrawals") {
        if (value === "Yes") {
          const nextRows = prev.lumpWithdrawals?.length
            ? prev.lumpWithdrawals.slice(0, 2)
            : [{ amount: "", age: "" }];
          return {
            ...prev,
            addLumpWithdrawals: "Yes",
            lumpWithdrawals: nextRows,
          };
        }
        return { ...prev, addLumpWithdrawals: "No", lumpWithdrawals: [] };
      }

      return { ...prev, [key]: value };
    });

    if (key === "dtcFirstYear")
      setTouched((t) => ({ ...t, dtcFirstYear: true }));
    if (key === "stopContributionAge")
      setTouched((t) => ({ ...t, stopContributionAge: true }));
  };

  const onChange = (key) => (e) => setField(key, e.target.value);

  /* ---------- Contributions rows ---------- */
  const updateLumpRow = (index, field, value) => {
    setForm((prev) => {
      const rows = Array.isArray(prev.lumpSums) ? [...prev.lumpSums] : [];
      rows[index] = {
        ...(rows[index] || { amount: "", age: "" }),
        [field]: value,
      };
      return { ...prev, lumpSums: rows };
    });
  };

  const addLumpRow = () => {
    setForm((prev) => {
      const rows = Array.isArray(prev.lumpSums) ? [...prev.lumpSums] : [];
      if (rows.length >= 8) return prev;
      rows.push({ amount: "", age: "" });
      return { ...prev, lumpSums: rows, addLumpSum: "Yes" };
    });
  };

  const removeLumpRow = (index) => {
    setForm((prev) => {
      const rows = Array.isArray(prev.lumpSums) ? [...prev.lumpSums] : [];
      rows.splice(index, 1);
      return { ...prev, lumpSums: rows };
    });

    setTouched((t) => {
      const next = { ...(t.lumpAge || {}) };
      delete next[index];
      return { ...t, lumpAge: next };
    });
  };

  /* ---------- Withdrawals rows ---------- */
  const updateWithdrawRow = (index, field, value) => {
    setForm((prev) => {
      const rows = Array.isArray(prev.lumpWithdrawals)
        ? [...prev.lumpWithdrawals]
        : [];
      rows[index] = {
        ...(rows[index] || { amount: "", age: "" }),
        [field]: value,
      };
      return { ...prev, lumpWithdrawals: rows };
    });
  };

  const addWithdrawRow = () => {
    setForm((prev) => {
      const rows = Array.isArray(prev.lumpWithdrawals)
        ? [...prev.lumpWithdrawals]
        : [];
      if (rows.length >= 2) return prev;
      rows.push({ amount: "", age: "" });
      return { ...prev, addLumpWithdrawals: "Yes", lumpWithdrawals: rows };
    });
  };

  const removeWithdrawRow = (index) => {
    setForm((prev) => {
      const rows = Array.isArray(prev.lumpWithdrawals)
        ? [...prev.lumpWithdrawals]
        : [];
      rows.splice(index, 1);
      return { ...prev, lumpWithdrawals: rows };
    });

    setTouched((t) => {
      const next = { ...(t.withdrawAge || {}) };
      delete next[index];
      return { ...t, withdrawAge: next };
    });
  };

  const handleReset = () => {
    setForm({
      youAre: "Un particulier, un membre de la famille ou un représentant personnel",
      openedYear: "2026",
      age: "",
      income: "",
      dtc: "Non, ou incertain(e)",

      dtcFirstYear: "",
      yearlyContribution: "",
      stopContributionAge: "49",
      growthBefore: "moderate_5",
      growthAfter: "moderate_5",
      addLumpSum: "No",
      lumpSums: [],
      withdrawalStartAge: "60",
      addLumpWithdrawals: "No",
      lumpWithdrawals: [],
    });

    setTouched({
      age: false,
      openedYear: false,
      dtcFirstYear: false,
      stopContributionAge: false,
      lumpAge: {},
      withdrawAge: {},
    });

    setReportReady(false);
    setReportLoading(false);
    setReportError("");
    setReportRaw(null);
    setAnnualRows([]);
    setEstimateLoading(false);
    setEstimateError("");
    setEstimateGrant(0);
    setEstimateBond(0);

    setLastGeneratedSig("");
    setTopPanelMode("graphs");
  };

  /* ---------- validations ---------- */
  const showDtcFirstYear = form.dtc === "Yes";

  const ageMin = 1;
  const ageMax = 59;
  const ageNum = form.age === "" ? NaN : Number(form.age);
  const ageHasValue = form.age !== "";
  const ageOutOfRange =
    ageHasValue && (Number.isNaN(ageNum) || ageNum < ageMin || ageNum > ageMax);
  const showAgeError = touched.age && ageOutOfRange;

  const hasNumericAge = Number.isFinite(ageNum);
  const incomeSuffix = hasNumericAge
    ? ageNum < 18
      ? "(avant 18 ans)"
      : "(après 18 ans)"
    : "";
  const incomeLabel = `Revenu net annuel ${incomeSuffix}`.trim();

  const openedYearMin = 2008;
  const openedYearMax = currentYear;

  const openedYearNum = form.openedYear === "" ? NaN : Number(form.openedYear);
  const openedYearHasValue = form.openedYear !== "";
  const openedYearOutOfRange =
    openedYearHasValue &&
    (Number.isNaN(openedYearNum) ||
      openedYearNum < openedYearMin ||
      openedYearNum > openedYearMax);

  const showOpenedYearError = touched.openedYear && openedYearOutOfRange;

  const openedYearValid = !openedYearOutOfRange && openedYearHasValue;

  const dtcMinYear = 2008;
  const dtcMaxYear = openedYearValid ? openedYearNum : currentYear;

  const dtcFirstYearNum =
    form.dtcFirstYear === "" ? NaN : Number(form.dtcFirstYear);
  const dtcFirstYearHasValue = form.dtcFirstYear !== "";
  const dtcFirstYearOutOfRange =
    dtcFirstYearHasValue &&
    (Number.isNaN(dtcFirstYearNum) ||
      dtcFirstYearNum < dtcMinYear ||
      dtcFirstYearNum > dtcMaxYear);
  const showDtcFirstYearError =
    showDtcFirstYear && touched.dtcFirstYear && dtcFirstYearOutOfRange;

  const stopMin = 30;
  const stopMax = 59;

  const stopAgeNum =
    form.stopContributionAge === "" ? NaN : Number(form.stopContributionAge);
  const stopAgeHasValue = form.stopContributionAge !== "";
  const stopAgeOutOfRange =
    stopAgeHasValue &&
    (Number.isNaN(stopAgeNum) || stopAgeNum < stopMin || stopAgeNum > stopMax);
  const showStopAgeError = touched.stopContributionAge && stopAgeOutOfRange;

  const yearlyTotal = numOrZero(form.yearlyContribution);

  useEffect(() => {
    const incomeVal = numOrZero(form.income);
    const contribVal = numOrZero(form.yearlyContribution);

    if (!(incomeVal > 0) || !(contribVal > 0)) {
      setEstimateLoading(false);
      setEstimateError("");
      setEstimateGrant(0);
      setEstimateBond(0);
      return;
    }

    let alive = true;
    const t = setTimeout(async () => {
      setEstimateLoading(true);
      setEstimateError("");

      try {
        const res = await fetch(RDSP_ESTIMATE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            annualNetIncome: incomeVal,
            annualContribution: contribVal,
          }),
        });

        const dataRaw = await res.json().catch(() => null);

        let data = dataRaw;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch {
            // keep as string if parsing fails
          }
        }

        if (!res.ok) {
          const msg =
            (data && (data.message || data.error)) ||
            `API error: ${res.status} ${res.statusText}`;
          throw new Error(msg);
        }

        const row0 =
          data?.data?.rows?.[0] || data?.rows?.[0] || data?.data || data || {};

        const g = numOrZero(
          pick(
            row0,
            ["Grant", "grant", "Annual_Grant", "annual_grant", "annualGrant"],
            data?.grant ?? data?.data?.grant ?? 0,
          ),
        );

        const b = numOrZero(
          pick(
            row0,
            ["Bond", "bond", "Annual_Bond", "annual_bond", "annualBond"],
            data?.bond ?? data?.data?.bond ?? 0,
          ),
        );

        if (!alive) return;

        setEstimateGrant(g);
        setEstimateBond(b);
        setEstimateError("");
      } catch (e) {
        if (!alive) return;
        setEstimateError(e?.message || "Estimate API failed.");
        setEstimateGrant(0);
        setEstimateBond(0);

        console.error("Estimate API error:", e);
      } finally {
        if (!alive) return;
        setEstimateLoading(false);
      }
    }, 450);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [form.income, form.yearlyContribution, RDSP_ESTIMATE_URL]);

  const grantDisplay = formatCurrency(estimateGrant);
  const bondDisplay = formatCurrency(estimateBond);
  const totalDisplay = formatCurrency(estimateGrant + estimateBond);

  const lumpAgeMin = Number.isFinite(ageNum) ? Math.max(50, ageNum) : 50;
  const lumpAgeMax = 59;

  const withdrawAgeMin = 30;
  const withdrawAgeMax = 99;

  /* ---------- Reports: API call ---------- */
  const buildReportPayload = () => {
    const annualInterestBefore = percentFromGrowthValue(form.growthBefore);
    const annualInterestAfter = percentFromGrowthValue(form.growthAfter);

    const contribList = (form.lumpSums || [])
      .map((r) => ({
        Year_No: Number(r.age),
        Lump_Sum_Contribution: numOrZero(r.amount),
      }))
      .filter(
        (x) =>
          Number.isFinite(x.Year_No) &&
          x.Year_No > 0 &&
          x.Lump_Sum_Contribution > 0,
      );

    const withdrawalList = (form.lumpWithdrawals || [])
      .map((r) => ({
        Year_No: Number(r.age),
        Lump_Sum_Withdrawal: numOrZero(r.amount),
      }))
      .filter(
        (x) =>
          Number.isFinite(x.Year_No) &&
          x.Year_No > 0 &&
          x.Lump_Sum_Withdrawal > 0,
      );

    return {
      beneficiaryAge: Number(form.age) || 0,
      rdspOpenedYear: Number(form.openedYear) || 0,
      dtcQualified: form.dtc === "Yes" ? "Yes" : "No",
      dtcopenYear:
        form.dtc === "Yes" && form.dtcFirstYear
          ? Number(form.dtcFirstYear)
          : null,
      annualNetIncome: numOrZero(form.income),

      annualContribution: numOrZero(form.yearlyContribution),
      contributeUntilAge: Number(form.stopContributionAge) || 49,

      investmentBeforeId: 1,
      investmentAfterId: 2,
      annualInterestBefore,
      annualInterestAfter,

      withdrawalStartAge: Number(form.withdrawalStartAge) || 60,
      withdrawalRate: 5,

      lumpSumContribList: contribList,
      lumpSumWithdrawalList: withdrawalList,
      lumpSumWithdrawList: withdrawalList,
    };
  };

  const buildReportFormData = () => {
    const payload = buildReportPayload();
    const data = new FormData();

    data.append("beneficiaryAge", String(payload.beneficiaryAge));
    data.append("rdspOpenedYear", String(payload.rdspOpenedYear));
    data.append("dtcQualified", String(payload.dtcQualified));
    data.append(
      "dtcopenYear",
      payload.dtcopenYear === null || payload.dtcopenYear === undefined
        ? ""
        : String(payload.dtcopenYear),
    );
    data.append("annualNetIncome", String(payload.annualNetIncome));
    data.append("annualContribution", String(payload.annualContribution));
    data.append("contributeUntilAge", String(payload.contributeUntilAge));
    data.append("investmentBeforeId", String(payload.investmentBeforeId));
    data.append("investmentAfterId", String(payload.investmentAfterId));
    data.append("annualInterestBefore", String(payload.annualInterestBefore));
    data.append("annualInterestAfter", String(payload.annualInterestAfter));
    data.append("withdrawalStartAge", String(payload.withdrawalStartAge));
    data.append("withdrawalRate", String(payload.withdrawalRate));

    (payload.lumpSumContribList || []).forEach((item) => {
      data.append("lumpSumContribList[]", JSON.stringify(item));
    });

    (payload.lumpSumWithdrawalList || []).forEach((item) => {
      data.append("lumpSumWithdrawalList[]", JSON.stringify(item));
    });

    (payload.lumpSumWithdrawList || []).forEach((item) => {
      data.append("lumpSumWithdrawList[]", JSON.stringify(item));
    });

    return data;
  };

  const currentReportSig = useMemo(() => {
    try {
      return JSON.stringify(buildReportPayload());
    } catch {
      return "";
    }
  }, [form]);

  useEffect(() => {
    if (!reportReady) return;
    if (!lastGeneratedSig) return;
    if (currentReportSig === lastGeneratedSig) return;

    setReportReady(false);
    setReportError("");
    setReportRaw(null);
    setAnnualRows([]);
    setReportLoading(false);

    setTopPanelMode("graphs");
  }, [currentReportSig, lastGeneratedSig, reportReady]);

  const handleGenerateReport = async () => {
    setReportError("");
    setReportLoading(true);

    try {
      const payload = buildReportPayload();
      const sig = JSON.stringify(payload);
      const formData = buildReportFormData();

      const res = await fetch(RDSP_REPORT_URL, {
        method: "POST",
        body: formData,
      });

      const rawText = await res.text().catch(() => "");
      let data = null;

      if (rawText) {
        try {
          data = JSON.parse(rawText);
        } catch {
          data = rawText;
        }
      }

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          // keep as plain string
        }
      }

      if (!res.ok) {
        const msg =
          (data && typeof data === "object" && (data.message || data.error)) ||
          rawText ||
          `API error: ${res.status} ${res.statusText}`;
        throw new Error(msg);
      }

      setReportRaw(data);

      const directRows =
        data?.data?.rows ||
        data?.rows ||
        data?.data?.data?.rows ||
        data?.result?.rows;

      const rows = Array.isArray(directRows)
        ? directRows
        : findAnnualRowsDeep(data);

      setAnnualRows(Array.isArray(rows) ? rows : []);
      setReportReady(true);

      setLastGeneratedSig(sig);
      setTopPanelMode("graphs");
    } catch (e) {
      setReportError(
        e?.message || "Something went wrong while generating the report.",
      );
      setReportReady(false);
      setReportRaw(null);
      setAnnualRows([]);
      setLastGeneratedSig("");
      setTopPanelMode("graphs");
    } finally {
      setReportLoading(false);
    }
  };

  /* ---------- Table columns ---------- */
  const tableColumns = useMemo(
    () => [
      {
        header: "Année",
        tip: helpText.reportYear,
        value: (r) =>
          pick(
            r,
            ["Calendar_Year", "calendar_year", "CalendarYear", "calendaryear"],
            pick(r, ["Year_No", "year_no"], ""),
          ),
      },

      {
        header: "Âge",
        tip: helpText.reportAge,
        value: (r) =>
          pick(r, ["Beneficiary_Age", "beneficiary_age", "Age", "age"], ""),
      },
      {
        header: "Cotisation",
        tip: helpText.reportContribution,
        value: (r) =>
          formatCurrency(
            numOrZero(
              pick(
                r,
                [
                  "Annual_Contribution",
                  "annual_contribution",
                  "Contribution",
                  "contribution",
                ],
                0,
              ),
            ),
          ),
      },
      {
        header: "Subvention",
        tip: helpText.reportGrant,
        value: (r) =>
          formatCurrency(
            numOrZero(
              pick(r, ["Annual_Grant", "annual_grant", "Grant", "grant"], 0),
            ),
          ),
      },
      {
        header: "Bon",
        tip: helpText.reportBond,
        value: (r) =>
          formatCurrency(
            numOrZero(
              pick(r, ["Annual_Bond", "annual_bond", "Bond", "bond"], 0),
            ),
          ),
      },
      {
        header: "Cotisation forfaitaire",
        tip: helpText.reportLumpSumContribution,
        value: (r) =>
          formatCurrency(
            numOrZero(
              pick(
                r,
                [
                  "Lump_Sum_Contribution",
                  "lump_sum_contribution",
                  "LumpSumContribution",
                  "lumpsumcontribution",
                ],
                0,
              ),
            ),
          ),
      },
      {
        header: "Total",
        tip: helpText.reportTotal,
        value: (r) => formatCurrency(numOrZero(pick(r, ["Total", "total"], 0))),
      },
      {
        header: "Intérêts",
        tip: helpText.reportInterest,
        value: (r) =>
          formatCurrency(
            numOrZero(
              pick(
                r,
                ["Annual_Interest", "annual_interest", "Interest", "interest"],
                0,
              ),
            ),
          ),
      },
      {
        header: "Retrait forfaitaire",
        tip: helpText.reportLumpSumWithdrawal,
        value: (r) =>
          formatCurrency(
            numOrZero(
              pick(
                r,
                [
                  "Lump_Sum_Withdrawal",
                  "lump_sum_withdrawal",
                  "LumpSumWithdrawal",
                  "lumpsumwithdrawal",
                ],
                0,
              ),
            ),
          ),
      },
      {
        header: "LDAP",
        tip: helpText.reportLDAP,
        value: (r) => formatCurrency(numOrZero(pick(r, ["LDAP", "ldap"], 0))),
      },
      {
        header: "Retrait total",
        tip: helpText.reportTotalWithdrawal,
        value: (r) =>
          formatCurrency(
            numOrZero(
              pick(
                r,
                [
                  "Annual_Withdrawal",
                  "annual_withdrawal",
                  "Total_Withdrawal",
                  "total_withdrawal",
                ],
                0,
              ),
            ),
          ),
      },
      {
        header: "Pénalité",
        tip: helpText.reportPenalty,
        value: (r) =>
          formatCurrency(numOrZero(pick(r, ["Penalty", "penalty"], 0))),
      },
      {
        header: "Solde",
        tip: helpText.reportBalance,
        value: (r) =>
          formatCurrency(
            numOrZero(
              pick(
                r,
                [
                  "Cumulative_Balance",
                  "cumulative_balance",
                  "Balance",
                  "balance",
                  "Closing_Balance",
                  "closing_balance",
                ],
                0,
              ),
            ),
          ),
      },
    ],
    [helpText],
  );

  /* ---------- Download menu ---------- */
  const downloadMenuRef = useRef(null);
  const [downloadOpen, setDownloadOpen] = useState(false);

  useEffect(() => {
    const onDoc = (e) => {
      if (!downloadMenuRef.current) return;
      if (!downloadMenuRef.current.contains(e.target)) setDownloadOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onDownloadCSV = () => {
    const csv = toCSV(annualRows || [], tableColumns);
    downloadTextFile("rdsp-detailed-annual-report.csv", csv, "text/csv");
    setDownloadOpen(false);
  };

  const onDownloadPDF = () => {
    const escapeHtml = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const rowsHtml = (annualRows || [])
      .map((r) => {
        const tds = tableColumns
          .map((c) => `<td>${escapeHtml(c.value(r))}</td>`)
          .join("");
        return `<tr>${tds}</tr>`;
      })
      .join("");

    const headHtml = tableColumns
      .map((c) => `<th>${escapeHtml(c.header)}</th>`)
      .join("");

    const html = `
      <html>
        <head>
          <title>Rapport annuel détaillé du REEI</title>
          <style>
            body { font-family: "Montserrat", sans-serif; padding: 24px; }
            h1 { font-size: 18px; margin: 0 0 16px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; white-space: nowrap; }
            th { background: #f6f8fb; }
          </style>
        </head>
        <body>
          <h1>RAPPORT ANNUEL DÉTAILLÉ DU REEI</h1>
          <table>
            <thead><tr>${headHtml}</tr></thead>
            <tbody>${rowsHtml || `<tr><td colspan="${tableColumns.length}">Aucune donnée</td></tr>`}</tbody>
          </table>
          <script>
            window.onload = () => { window.print(); };
          </script>
        </body>
      </html>
    `;

    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();

    setDownloadOpen(false);
  };

  /*  Graph + Summary  */
  const summaryComputed = useMemo(() => {
    const rows = Array.isArray(annualRows) ? annualRows : [];

    const sumAnnualContrib = rows.reduce(
      (s, r) =>
        s +
        numOrZero(
          pick(
            r,
            [
              "Annual_Contribution",
              "annual_contribution",
              "Contribution",
              "contribution",
            ],
            0,
          ),
        ),
      0,
    );
    const sumLumpContrib = rows.reduce(
      (s, r) =>
        s +
        numOrZero(
          pick(
            r,
            [
              "Lump_Sum_Contribution",
              "lump_sum_contribution",
              "LumpSumContribution",
              "lumpsumcontribution",
            ],
            0,
          ),
        ),
      0,
    );

    const sumGrant = rows.reduce(
      (s, r) =>
        s +
        numOrZero(
          pick(r, ["Annual_Grant", "annual_grant", "Grant", "grant"], 0),
        ),
      0,
    );
    const sumBond = rows.reduce(
      (s, r) =>
        s +
        numOrZero(pick(r, ["Annual_Bond", "annual_bond", "Bond", "bond"], 0)),
      0,
    );
    const sumInterest = rows.reduce(
      (s, r) =>
        s +
        numOrZero(
          pick(
            r,
            ["Annual_Interest", "annual_interest", "Interest", "interest"],
            0,
          ),
        ),
      0,
    );
    const sumLDAP = rows.reduce(
      (s, r) => s + numOrZero(pick(r, ["LDAP", "ldap"], 0)),
      0,
    );

    const totalContributionMade = sumAnnualContrib + sumLumpContrib;
    const totalGovBenefits = sumGrant + sumBond;

    const series = rows
      .map((r) => {
        const age = toNumberLoose(
          pick(r, ["Beneficiary_Age", "beneficiary_age", "Age", "age"], NaN),
        );
        const bal = numOrZero(
          pick(
            r,
            [
              "Cumulative_Balance",
              "cumulative_balance",
              "Balance",
              "balance",
              "Closing_Balance",
              "closing_balance",
            ],
            0,
          ),
        );
        return { x: Number.isFinite(age) ? age : NaN, y: bal };
      })
      .filter((p) => Number.isFinite(p.x));

    return {
      totals: {
        contributions: totalContributionMade,
        govBenefits: totalGovBenefits,
        interest: sumInterest,
      },
      summaryTiles: {
        beneficiaryAge: form.age ? String(form.age) : "-",
        yearOpened: form.openedYear ? String(form.openedYear) : "-",
        annualNetIncome: formatCurrency(numOrZero(form.income)),
        strategyBeforeAfter: `${percentFromGrowthValue(form.growthBefore)}% / ${percentFromGrowthValue(form.growthAfter)}%`,
        totalContributionMade: formatCurrency(totalContributionMade),
        totalGovGrant: formatCurrency(sumGrant),
        totalGovBond: formatCurrency(sumBond),
        totalInterest: formatCurrency(sumInterest),
        totalLDAP: formatCurrency(sumLDAP),
      },
      linePoints: series,
    };
  }, [
    annualRows,
    form.age,
    form.openedYear,
    form.income,
    form.growthBefore,
    form.growthAfter,
  ]);

  const donutItems = useMemo(() => {
    return [
      {
        label: "Cotisations",
        value: summaryComputed.totals.contributions,
        color: "#9fc5f0",
      },
      {
        label: "Prestations gouvernementales",
        value: summaryComputed.totals.govBenefits,
        color: "#00adef",
      },
      {
        label: "Intérêts",
        value: summaryComputed.totals.interest,
        color: "#4f86ff",
      },
    ];
  }, [summaryComputed]);

  return (
  <>
    <SEO
      title="Calculateur REEI | einsured.ca Canada"
      description="Utilisez le calculateur REEI d'einsured.ca pour estimer la croissance de votre régime enregistré d'épargne-invalidité et planifier un avenir financier plus sûr."
    />

    <div className="bg-white">
      <section className="py-10 md:py-14 px-2 sm:px-4 md:px-6 lg:px-8 bg-white">
        <div className="w-full max-w-none mx-auto">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-[#49628b]">
              Calculateur REEI Einsured
            </h1>
          </div>

          <div className="mt-4 max-w-4xl mx-auto text-center text-sm sm:text-base text-gray-700 leading-relaxed">
            {disclaimerShort}
          </div>

          <div className="mt-6 bg-white px-0 py-0">
            <div className="space-y-6">
              <SectionCard title="Coordonnées du bénéficiaire">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Vous êtes</span> <InfoTip text={helpText.youAre} />
                    </label>
                    <select
                      value={form.youAre}
                      onChange={onChange("youAre")}
                      className={inputClasses}
                    >
                      <option>
                        Un particulier, un membre de la famille ou un représentant personnel
                      </option>
                      <option>Un professionnel financier, juridique ou autre</option>
                      <option>Organisme sans but lucratif</option>
                      <option>Autre</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Année d'ouverture du REEI</span>{" "}
                      <InfoTip text={helpText.openedYear} />
                    </label>
                    <input
                      type="number"
                      value={form.openedYear}
                      onChange={onChange("openedYear")}
                      onBlur={() =>
                        setTouched((t) => ({ ...t, openedYear: true }))
                      }
                      inputMode="numeric"
                      className={[
                        inputClasses,
                        showOpenedYearError
                          ? "border-rose-400 focus:ring-rose-200 focus:border-rose-400"
                          : "",
                      ].join(" ")}
                      placeholder="e.g., 2020"
                      min={openedYearMin}
                      max={openedYearMax}
                    />
                    {showOpenedYearError ? (
                      <p className="mt-2 text-sm text-rose-600">
                        L'année doit être comprise entre {openedYearMin} et {openedYearMax}
                        .
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Âge actuel du bénéficiaire</span>{" "}
                      <InfoTip text={helpText.age} />
                    </label>
                    <input
                      type="number"
                      value={form.age}
                      onChange={onChange("age")}
                      onBlur={() => setTouched((t) => ({ ...t, age: true }))}
                      className={[
                        inputClasses,
                        showAgeError
                          ? "border-rose-400 focus:ring-rose-200 focus:border-rose-400"
                          : "",
                      ].join(" ")}
                      placeholder="e.g., 23"
                      min={ageMin}
                      max={ageMax}
                    />
                    {showAgeError ? (
                      <p className="mt-2 text-sm text-rose-600">
                        L'âge doit être compris entre {ageMin} et {ageMax}.
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-gray-600">
                        Les cotisations/subventions s'arrêtent à la fin de l'année
                        où ils atteignent 49 ans ; les retraits commencent généralement après 60 ans.
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>{incomeLabel}</span>{" "}
                      <InfoTip text={helpText.income} />
                    </label>

                    <div className="mt-1 flex w-full overflow-hidden rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-[#00adef] focus-within:border-[#00adef]">
                      <div className="flex w-12 items-center justify-center border-r border-gray-300 text-gray-600 font-semibold">
                        $
                      </div>
                      <input
                        value={form.income}
                        onChange={onChange("income")}
                        inputMode="numeric"
                        className="w-full px-3 py-2 text-sm sm:text-base text-gray-800 outline-none"
                        placeholder="e.g., 50,000"
                      />
                    </div>

                    <p className="mt-2 text-sm text-gray-600">
                      Ceci aide à déterminer les niveaux d'admissibilité à la SCEI/BCEI.
                    </p>
                  </div>

                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>
                        Admissible au crédit d'impôt pour personnes handicapées (CIPH) ?
                      </span>{" "}
                      <InfoTip text={helpText.dtc} />
                    </label>

                    <ToggleButtonGroup
                      value={form.dtc}
                      onChange={(v) => setField("dtc", v)}
                      maxWidthClass="sm:max-w-md"
                      options={[
                        { value: "Non, ou incertain(e)", label: "Non, ou incertain(e)" },
                        { value: "Yes", label: "Oui" },
                      ]}
                    />
                  </div>

                  {showDtcFirstYear ? (
                    <div>
                      <label
                        className={`${labelClasses} flex items-center gap-1`}
                      >
                        <span>Première année d'approbation du CIPH</span>{" "}
                        <InfoTip text={helpText.dtcFirstYear} />
                      </label>
                      <input
                        type="number"
                        value={form.dtcFirstYear}
                        onChange={onChange("dtcFirstYear")}
                        onBlur={() =>
                          setTouched((t) => ({ ...t, dtcFirstYear: true }))
                        }
                        inputMode="numeric"
                        className={[
                          inputClasses,
                          showDtcFirstYearError
                            ? "border-rose-400 focus:ring-rose-200 focus:border-rose-400"
                            : "",
                        ].join(" ")}
                        placeholder="YYYY"
                        min={dtcMinYear}
                        max={dtcMaxYear}
                      />
                      {showDtcFirstYearError ? (
                        <p className="mt-2 text-sm text-rose-600">
                          Doit être comprise entre {dtcMinYear} et {dtcMaxYear}.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </SectionCard>

              <SectionCard title="Cotisations">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Cotisation annuelle au REEI</span>{" "}
                      <InfoTip text={helpText.yearlyContribution} />
                    </label>

                    <div className="mt-1 flex w-full overflow-hidden rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-[#00adef] focus-within:border-[#00adef]">
                      <div className="flex w-12 items-center justify-center border-r border-gray-300 text-gray-600 font-semibold">
                        $
                      </div>
                      <input
                        value={form.yearlyContribution}
                        onChange={onChange("yearlyContribution")}
                        inputMode="numeric"
                        className="w-full px-3 py-2 text-sm sm:text-base text-gray-800 outline-none"
                        placeholder="e.g., 1,000"
                      />
                    </div>

                    <p className="mt-2 text-sm text-gray-600">
                      Subvention : {grantDisplay} · Bon : {bondDisplay} · Total :{" "}
                      {totalDisplay}
                    </p>
                  </div>

                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Âge d'arrêt des cotisations</span>{" "}
                      <InfoTip text={helpText.stopContributionAge} />
                    </label>
                    <input
                      type="number"
                      value={form.stopContributionAge}
                      onChange={onChange("stopContributionAge")}
                      onBlur={() =>
                        setTouched((t) => ({ ...t, stopContributionAge: true }))
                      }
                      className={[
                        inputClasses,
                        showStopAgeError
                          ? "border-rose-400 focus:ring-rose-200 focus:border-rose-400"
                          : "",
                      ].join(" ")}
                      min={stopMin}
                      max={stopMax}
                    />
                    {showStopAgeError ? (
                      <p className="mt-2 text-sm text-rose-600">
                        Doit être compris entre {stopMin} et {stopMax}.
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Taux de croissance avant les retraits</span>{" "}
                      <InfoTip text={helpText.growthBefore} />
                    </label>
                    <select
                      value={form.growthBefore}
                      onChange={onChange("growthBefore")}
                      className={inputClasses}
                    >
                      {growthOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Taux de croissance après les retraits</span>{" "}
                      <InfoTip text={helpText.growthAfter} />
                    </label>
                    <select
                      value={form.growthAfter}
                      onChange={onChange("growthAfter")}
                      className={inputClasses}
                    >
                      {growthOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Ajouter des cotisations forfaitaires ?</span>{" "}
                      <InfoTip text={helpText.addLumpSum} />
                    </label>

                    <ToggleButtonGroup
                      value={form.addLumpSum}
                      onChange={(v) => setField("addLumpSum", v)}
                      options={[
                        { value: "No", label: "Non" },
                        { value: "Yes", label: "Oui" },
                      ]}
                    />

                    <p className="mt-2 text-sm text-gray-600">
                      Jusqu'à 8 lignes. Chaque ligne nécessite un montant et l'âge du bénéficiaire.
                    </p>
                  </div>

                  {form.addLumpSum === "Yes" && (
                    <div className="md:col-span-2 lg:col-span-3 space-y-6">
                      {form.lumpSums.map((row, idx) => {
                        const rowAgeNum =
                          row.age === "" ? NaN : Number(row.age);
                        const rowAgeHasValue = row.age !== "";
                        const rowAgeOutOfRange =
                          rowAgeHasValue &&
                          (Number.isNaN(rowAgeNum) ||
                            rowAgeNum < lumpAgeMin ||
                            rowAgeNum > lumpAgeMax);
                        const showRowAgeError =
                          !!touched.lumpAge?.[idx] &&
                          rowAgeHasValue &&
                          rowAgeOutOfRange;

                        return (
                          <div
                            key={idx}
                            className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-x-8 md:items-end"
                          >
                            <div className="min-w-0">
                              <label
                                className={`${labelClasses} flex items-center gap-1`}
                              >
                                <span>Montant</span>{" "}
                                <InfoTip text={helpText.lumpAmount} />
                              </label>
                              <div className="mt-1 flex w-full overflow-hidden rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-[#00adef] focus-within:border-[#00adef]">
                                <div className="flex w-12 items-center justify-center border-r border-gray-300 text-gray-600 font-semibold">
                                  $
                                </div>
                                <input
                                  value={row.amount}
                                  onChange={(e) =>
                                    updateLumpRow(idx, "amount", e.target.value)
                                  }
                                  inputMode="numeric"
                                  className="w-full px-3 py-2 text-sm sm:text-base text-gray-800 outline-none"
                                  placeholder="e.g., 5,000"
                                />
                              </div>
                            </div>

                            <div className="min-w-0">
                              <label
                                className={`${labelClasses} flex items-center gap-1`}
                              >
                                <span>Âge du bénéficiaire lors de l'année du dépôt</span>{" "}
                                <InfoTip text={helpText.lumpAge} />
                              </label>
                              <input
                                type="number"
                                value={row.age}
                                onChange={(e) => {
                                  updateLumpRow(idx, "age", e.target.value);
                                  setTouched((t) => ({
                                    ...t,
                                    lumpAge: {
                                      ...(t.lumpAge || {}),
                                      [idx]: true,
                                    },
                                  }));
                                }}
                                onBlur={() =>
                                  setTouched((t) => ({
                                    ...t,
                                    lumpAge: {
                                      ...(t.lumpAge || {}),
                                      [idx]: true,
                                    },
                                  }))
                                }
                                className={[
                                  inputClasses,
                                  showRowAgeError
                                    ? "border-rose-400 focus:ring-rose-200 focus:border-rose-400"
                                    : "",
                                ].join(" ")}
                                placeholder={`≥ ${lumpAgeMin}`}
                              />
                              {showRowAgeError ? (
                                <p className="mt-2 text-sm text-rose-600">
                                  L'âge doit être compris entre {lumpAgeMin} et{" "}
                                  {lumpAgeMax}.
                                </p>
                              ) : null}
                            </div>

                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => removeLumpRow(idx)}
                                className="mt-2 md:mt-0 inline-flex items-center justify-center px-6 py-2 rounded-full bg-white border border-gray-300 text-gray-700 font-semibold text-sm sm:text-base hover:bg-gray-100 transition-colors md:justify-self-start"
                              >
                                Retirer
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={addLumpRow}
                          disabled={form.lumpSums.length >= 8}
                          className={[
                            "inline-flex items-center justify-center px-6 py-2 rounded-full border font-semibold text-sm sm:text-base transition-colors",
                            form.lumpSums.length >= 8
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100",
                          ].join(" ")}
                        >
                          <span className="text-lg leading-none mr-2">＋</span>{" "}
                          Ajouter une ligne
                        </button>

                        <span className="text-sm text-gray-600">
                          Max {Math.min(8, form.lumpSums.length || 0)}/8 lignes.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>

              <SectionCard title="Retraits">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Âge de début des retraits</span>{" "}
                      <InfoTip text={helpText.withdrawalStartAge} />
                    </label>
                    <input
                      value={form.withdrawalStartAge}
                      readOnly
                      className={
                        inputClasses + " bg-gray-100 cursor-not-allowed"
                      }
                    />
                    <p className="mt-2 text-sm text-gray-600">
                      Ceci définit l'âge minimum autorisé pour tout retrait
                      forfaitaire.
                    </p>
                  </div>

                  <div>
                    <label
                      className={`${labelClasses} flex items-center gap-1`}
                    >
                      <span>Ajouter des retraits forfaitaires ?</span>{" "}
                      <InfoTip text={helpText.addLumpWithdrawals} />
                    </label>

                    <ToggleButtonGroup
                      value={form.addLumpWithdrawals}
                      onChange={(v) => setField("addLumpWithdrawals", v)}
                      options={[
                        { value: "No", label: "Non" },
                        { value: "Yes", label: "Oui" },
                      ]}
                    />

                    <p className="mt-2 text-sm text-gray-600">
                      Jusqu'à 2 lignes. L'âge doit être ≥ {withdrawAgeMin}.
                    </p>
                  </div>

                  {form.addLumpWithdrawals === "Yes" && (
                    <div className="md:col-span-2 lg:col-span-3 space-y-6">
                      {form.lumpWithdrawals.map((row, idx) => {
                        const rowAgeNum =
                          row.age === "" ? NaN : Number(row.age);
                        const rowAgeHasValue = row.age !== "";
                        const rowAgeOutOfRange =
                          rowAgeHasValue &&
                          (Number.isNaN(rowAgeNum) ||
                            rowAgeNum < withdrawAgeMin ||
                            rowAgeNum > withdrawAgeMax);
                        const showRowAgeError =
                          !!touched.withdrawAge?.[idx] &&
                          rowAgeHasValue &&
                          rowAgeOutOfRange;

                        return (
                          <div
                            key={idx}
                            className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-x-8 md:items-end"
                          >
                            <div className="min-w-0">
                              <label
                                className={`${labelClasses} flex items-center gap-1`}
                              >
                                <span>Montant</span>{" "}
                                <InfoTip text={helpText.withdrawAmount} />
                              </label>
                              <div className="mt-1 flex w-full overflow-hidden rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-[#00adef] focus-within:border-[#00adef]">
                                <div className="flex w-12 items-center justify-center border-r border-gray-300 text-gray-600 font-semibold">
                                  $
                                </div>
                                <input
                                  value={row.amount}
                                  onChange={(e) =>
                                    updateWithdrawRow(
                                      idx,
                                      "amount",
                                      e.target.value,
                                    )
                                  }
                                  inputMode="numeric"
                                  className="w-full px-3 py-2 text-sm sm:text-base text-gray-800 outline-none"
                                  placeholder="e.g., 2,000"
                                />
                              </div>
                            </div>

                            <div className="min-w-0">
                              <label
                                className={`${labelClasses} flex items-center gap-1`}
                              >
                                <span>
                                  Âge du bénéficiaire lors de l'année du retrait
                                </span>{" "}
                                <InfoTip text={helpText.withdrawAge} />
                              </label>
                              <input
                                type="number"
                                value={row.age}
                                onChange={(e) => {
                                  updateWithdrawRow(idx, "age", e.target.value);
                                  setTouched((t) => ({
                                    ...t,
                                    withdrawAge: {
                                      ...(t.withdrawAge || {}),
                                      [idx]: true,
                                    },
                                  }));
                                }}
                                onBlur={() =>
                                  setTouched((t) => ({
                                    ...t,
                                    withdrawAge: {
                                      ...(t.withdrawAge || {}),
                                      [idx]: true,
                                    },
                                  }))
                                }
                                className={[
                                  inputClasses,
                                  showRowAgeError
                                    ? "border-rose-400 focus:ring-rose-200 focus:border-rose-400"
                                    : "",
                                ].join(" ")}
                                placeholder={`≥ ${withdrawAgeMin}`}
                              />
                              {showRowAgeError ? (
                                <p className="mt-2 text-sm text-rose-600">
                                  L'âge doit être compris entre {withdrawAgeMin} et{" "}
                                  {withdrawAgeMax}.
                                </p>
                              ) : null}
                            </div>

                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => removeWithdrawRow(idx)}
                                className="mt-2 md:mt-0 inline-flex items-center justify-center px-6 py-2 rounded-full bg-white border border-gray-300 text-gray-700 font-semibold text-sm sm:text-base hover:bg-gray-100 transition-colors md:justify-self-start"
                              >
                                Retirer
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={addWithdrawRow}
                          disabled={form.lumpWithdrawals.length >= 2}
                          className={[
                            "inline-flex items-center justify-center px-6 py-2 rounded-full border font-semibold text-sm sm:text-base transition-colors",
                            form.lumpWithdrawals.length >= 2
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                              : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100",
                          ].join(" ")}
                        >
                          <span className="text-lg leading-none mr-2">＋</span>{" "}
                          Ajouter une ligne
                        </button>

                        <span className="text-sm text-gray-600">
                          Max {Math.min(2, form.lumpWithdrawals.length || 0)}/2
                          lignes.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </SectionCard>

              {!reportReady && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="inline-flex w-full sm:w-auto items-center justify-center px-8 sm:px-10 py-3 rounded-full bg-white border border-gray-300 text-gray-700 font-semibold text-base sm:text-lg hover:bg-gray-100 transition-colors"
                    >
                      Réinitialiser
                    </button>

                    <button
                      type="button"
                      onClick={handleGenerateReport}
                      disabled={reportLoading}
                      className={[
                        "inline-flex w-full sm:w-auto items-center justify-center px-8 sm:px-10 py-3 rounded-full bg-[#435D8C] hover:bg-[#31446e] text-white font-semibold text-base sm:text-lg shadow-md transition-colors text-center",
                        reportLoading
                          ? "opacity-70 cursor-not-allowed hover:bg-[#435D8C]"
                          : "",
                      ].join(" ")}
                    >
                      {reportLoading ? "Génération en cours..." : "Générer le rapport"}
                    </button>
                  </div>

                  {reportError ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 font-semibold">
                      {reportError}
                    </div>
                  ) : null}
                </div>
              )}

              {reportReady && (
                <SectionCard
                  title={topPanelMode === "graphs" ? "GRAPHIQUES" : "RÉSUMÉ"}
                  right={
                    <button
                      type="button"
                      onClick={() =>
                        setTopPanelMode((m) =>
                          m === "graphs" ? "summary" : "graphs",
                        )
                      }
                      className="inline-flex items-center justify-center px-8 py-3 rounded-xl bg-white border border-[#d7e7ff] text-[#2f4f77] font-semibold text-base hover:bg-[#f6fbff] transition-colors"
                    >
                      {topPanelMode === "graphs" ? "Résumé" : "Graphiques"}
                    </button>
                  }
                >
                  <div className="rounded-2xl border border-[#00adef] bg-white px-4 sm:px-6 py-6">
                    {topPanelMode === "graphs" ? (
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                        <div className="lg:col-span-4 flex items-center justify-center">
                          <MiniDonut items={donutItems} />
                        </div>

                        <div className="lg:col-span-8">
                          <AreaLineChart points={summaryComputed.linePoints} />
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-[#d7e7ff] bg-white p-4 sm:p-5 max-h-[320px] overflow-y-auto">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <SummaryTile
                            title="Âge du bénéficiaire"
                            value={summaryComputed.summaryTiles.beneficiaryAge}
                          />
                          <SummaryTile
                            title="Stratégie avant/après les retraits"
                            value={
                              summaryComputed.summaryTiles.strategyBeforeAfter
                            }
                          />
                          <SummaryTile
                            title="Total des bons gouvernementaux reçus"
                            value={summaryComputed.summaryTiles.totalGovBond}
                          />

                          <SummaryTile
                            title="Année d'ouverture du REEI"
                            value={summaryComputed.summaryTiles.yearOpened}
                          />
                          <SummaryTile
                            title="Total des cotisations versées"
                            value={
                              summaryComputed.summaryTiles.totalContributionMade
                            }
                          />
                          <SummaryTile
                            title="Total des intérêts gagnés"
                            value={summaryComputed.summaryTiles.totalInterest}
                          />

                          <SummaryTile
                            title="Revenu net annuel"
                            value={summaryComputed.summaryTiles.annualNetIncome}
                          />
                          <SummaryTile
                            title="Total des subventions gouvernementales reçues"
                            value={summaryComputed.summaryTiles.totalGovGrant}
                          />
                          <SummaryTile
                            title="Total des paiements LDAP viagères"
                            value={summaryComputed.summaryTiles.totalLDAP}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </SectionCard>
              )}

              {reportReady && (
                <SectionCard
                  title="RAPPORT ANNUEL DÉTAILLÉ DU REEI"
                  right={
                    <div className="relative" ref={downloadMenuRef}>
                      <button
                        type="button"
                        onClick={() => setDownloadOpen((v) => !v)}
                        className="inline-flex items-center justify-center px-6 py-2 rounded-full border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-100 transition-colors"
                      >
                        Télécharger <span className="text-xs ml-2">▼</span>
                      </button>

                      {downloadOpen && (
                        <div className="absolute right-0 mt-2 w-44 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden z-10">
                          <button
                            type="button"
                            onClick={onDownloadPDF}
                            className="w-full px-4 py-3 text-left text-sm hover:bg-gray-100"
                          >
                            PDF
                          </button>
                          <button
                            type="button"
                            onClick={onDownloadCSV}
                            className="w-full px-4 py-3 text-left text-sm hover:bg-gray-100"
                          >
                            CSV
                          </button>
                        </div>
                      )}
                    </div>
                  }
                >
                  <div className="px-4 sm:px-0">
                    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="min-w-[1300px] w-full text-sm">
                          <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                            <tr>
                              {tableColumns.map((c) => {
                                const isWrapHeader =
                                  c.header === "Cotisation forfaitaire" ||
                                  c.header === "Retrait forfaitaire";

                                return (
                                  <th
                                    key={c.header}
                                    className={[
                                      "px-4 py-3 text-left font-semibold text-[#2f4f77]",
                                      isWrapHeader
                                        ? "whitespace-normal"
                                        : "whitespace-nowrap",
                                    ].join(" ")}
                                  >
                                    {isWrapHeader ? (
                                      <div className="flex flex-col leading-tight">
                                        <span>Forfaitaire</span>
                                        <span className="inline-flex items-center">
                                          {c.header === "Cotisation forfaitaire"
                                            ? "Cotisation"
                                            : "Retrait"}
                                          {c.tip ? (
                                            <InfoTip text={c.tip} />
                                          ) : null}
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="inline-flex items-center">
                                        <span>{c.header}</span>
                                        {c.tip ? (
                                          <InfoTip text={c.tip} />
                                        ) : null}
                                      </div>
                                    )}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {(annualRows || []).map((r, i) => (
                              <tr key={i} className="border-b border-gray-100">
                                {tableColumns.map((c) => (
                                  <td
                                    key={c.header}
                                    className="px-4 py-3 whitespace-nowrap text-gray-800"
                                  >
                                    {c.value(r)}
                                  </td>
                                ))}
                              </tr>
                            ))}

                            {!annualRows?.length ? (
                              <tr>
                                <td
                                  className="px-4 py-6 text-gray-600"
                                  colSpan={tableColumns.length}
                                >
                                  Aucune ligne annuelle détectée dans la réponse de l'API.
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>

                      <div className="px-4 py-4 flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => navigate("/")}
                          className="inline-flex w-full sm:w-auto items-center justify-center px-8 sm:px-10 py-3 rounded-full bg-[#435D8C] hover:bg-[#31446e] text-white font-semibold text-base sm:text-lg shadow-md transition-colors text-center"
                        >
                          Terminer
                        </button>
                      </div>
                    </div>
                    <div className=" mt-3 flex flex-wrap items-center justify-center gap-2 text-base sm:text-lg ">
                      <span>Propulsé par</span>

                      <a
                        href="https://squaretrigger.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0  hover:text-sky-600 transition-colors"
                      >
                        <span>Square Trigger</span>

                        <img
                          src={img90}
                          alt="Square Trigger logo"
                          className="h-7 sm:h-8 w-auto"
                          loading="lazy"
                        />
                      </a>
                    </div>
                  </div>
                </SectionCard>
              )}
            </div>
          </div>
        </div>
      </section>

      {disclaimerOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9998] flex items-center justify-center px-3 sm:px-6">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setDisclaimerOpen(false)}
            />

            <div className="relative z-[9999] w-full max-w-3xl rounded-2xl bg-white shadow-2xl overflow-hidden">
              <div className="flex items-start justify-between gap-4 px-6 py-5 border-b">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-500 mt-[2px]" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Clause de non-responsabilité
                    </h3>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setDisclaimerOpen(false)}
                  className="text-gray-500 hover:text-gray-800 text-2xl leading-none"
                  aria-label="Fermer"
                >
                  ×
                </button>
              </div>

              <div className="max-h-[70vh] overflow-y-auto px-6 py-5 text-gray-800 leading-relaxed">
                {disclaimerFull}
              </div>

              <div className="px-6 py-4 border-t flex justify-end">
                <button
                  type="button"
                  onClick={() => setDisclaimerOpen(false)}
                  className="inline-flex w-full sm:w-auto items-center justify-center px-8 sm:px-10 py-3 rounded-full bg-[#435D8C] hover:bg-[#31446e] text-white font-semibold text-base sm:text-lg shadow-md transition-colors text-center"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
        </div>
  </>
);
}
